/**
 * Digital Twin client API
 */

export type DigitalTwinResponse = {
	serialNumber: string
	partNumber: string
	nonIecPartNumber: string
	family: string
	familyId: string
	name: string
	type: string
	image: string
	link: string
	shippingDate: string
	handcrafter: any[]
	cadFiles: {
		'@odata.context': string
		'@odata.count': number
		value: CadFile[]
	}
	userManuals: {
		'@odata.context': string
		'@odata.count': number
		value: UserManual[]
	}
	safetyInstructions: {
		'@odata.context': string
		'@odata.count': number
		value: SafetyInstructions[]
	}
	maintenanceCycles: MaintenanceCycles
	calibrationCertificates: {
		tmss: any
		original: {
			value: CalibrationCertificate[]
		}
	}
	complianceCertificates: {
		'@odata.context': string
		'@odata.count': number
		value: ComplianceCertificate[]
	}
	zipLink: string
	otherProjectTwins: any[]
	token: string
}

export type File = {
	'@search.score': number
	tool_partnumber: string
	item_type: string
	metadata_storage_name: string
	metadata_storage_content_type: string
	metadata_storage_last_modified: string
	metadata_storage_size: number
	item_creation_date: Date
	key: string
	item_path: string
}

export type CadFile = File & {
	tool_partnumber: string
}

export type UserManual = File & {
	tool_partnumbers: string
	tool_type: string | null
	item_description: string
	item_version: string
	item_language: ItemLanguage
}

export type CatalogLeaflet = File & {
	tool_partnumbers: string
	tool_type: string | null
	item_language: ItemLanguage
}

export type SafetyInstructions = UserManual

export type CalibrationCertificate = Omit<File, 'item_type'> & {
	tool_serialnumber: string
	tool_projectnumber: string | null
	tool_stref: string | null
	tool_type: string | null
}

export type ComplianceCertificate = File & {
	tool_partnumbers: string
	tool_type: string
	item_validity_date: string
	item_state: string
	item_marking: string
	item_countries: string
	item_version: string | null
}

export type ExplodedView = UserManual

export type MaintenanceCycles = {
	low: Cycle[]
	medium: Cycle[]
	high: Cycle[]
}

export type Cycle = {
	cycles_threshold: number
	services: number[]
}

export type ItemLanguage =
	| 'BG'
	| 'CS'
	| 'DE'
	| 'EN'
	| 'ES'
	| 'FR'
	| 'HU'
	| 'IT'
	| 'JA'
	| 'KO'
	| 'PL'
	| 'RO'
	| 'SK'
	| 'SL'
	| 'SV'
	| 'ZH'
	| 'Multi'

export const mapLanguageAndLocale: Record<string, ItemLanguage[]> = {
	en: ['EN'],
	fr: ['FR', 'EN'],
	de: ['DE', 'EN'],
	es: ['ES', 'EN'],
	en_US: ['EN'],
	en_GB: ['EN'],
	en_IN: ['EN'],
	en_TH: ['EN'],
	en_ZA: ['EN'],
	de_AT: ['DE', 'EN'],
	de_DE: ['DE', 'EN'],
	de_CH: ['DE', 'EN'],
	fr_FR: ['FR', 'EN'],
	fr_BE: ['FR', 'EN'],
	fr_CA: ['FR', 'EN'],
	fr_CH: ['FR', 'EN'],
	es_ES: ['ES', 'EN'],
	es_AR: ['ES', 'EN'],
	es_CL: ['ES', 'EN'],
	es_CO: ['ES', 'EN'],
	es_PE: ['ES', 'EN'],
	es_MX: ['ES', 'EN'],
	cs_CZ: ['CS', 'EN'],
	it_IT: ['IT', 'EN'],
	it_CH: ['IT', 'EN'],
	sk_SK: ['SK', 'EN'],
	hu_HU: ['HU', 'EN'],
	sl_SI: ['SL', 'EN'],
	ro_RO: ['RO', 'EN'],
	pl_PL: ['PL', 'EN'],
	ko_KR: ['KO', 'EN'],
	ja_JP: ['JA', 'EN'],
	zh_CN: ['ZH', 'EN'],
	tr_TR: ['EN'],
	ru_RU: ['EN'],
	da_DK: ['EN'],
	sv_SE: ['SV', 'EN'],
	nl_BE: ['EN'],
	nl_NL: ['EN'],
	th_TH: ['EN'],
	pt_PT: ['EN'],
	pt_BR: ['EN'],
}

export async function getProductDocumentsBySerialNumber(
	serialNumber: string,
	lang?: string,
	abortController: AbortController = null,
): Promise<DigitalTwinResponse> {
	const response = await fetch(
		`${
			process.env.NEXT_PUBLIC_DIGITAL_TWIN_URL
		}/api/standard/${serialNumber}?locale=${lang ?? 'en_US'}`,
		{
			signal: abortController?.signal,
			headers: {
				'X-API-KEY': process.env.NEXT_PUBLIC_DESOUTTER_API_KEY,
			},
		},
	)

	const data = await response.json()

	if (response.status !== 200) {
		throw data.dato_message
	}

	return data
}

export async function getProductDocumentsByPartNumber(
	partNumber: string,
	lang?: string,
	abortController: AbortController = null,
): Promise<DigitalTwinResponse> {
	const response = await fetch(
		`${
			process.env.NEXT_PUBLIC_DIGITAL_TWIN_URL
		}/api/documents/${partNumber}?locale=${lang ?? 'en_US'}`,
		{
			signal: abortController?.signal,
			headers: {
				'X-API-KEY': process.env.NEXT_PUBLIC_DESOUTTER_API_KEY,
			},
		},
	)

	const data = await response.json()

	if (response.status !== 200) {
		throw data.dato_message
	}

	return data
}
